// HR & Workforce — reference data for the HR page and the Social topic forms.
//
// HR_METRICS is the one list of aggregate HR figures Sustina keeps per company and
// period (HR_SYNC.Metric). They come from a connected HR system, from hand entry,
// or (later) from the company's own HR codes once mapped. Each metric names the
// Social topic fields it fills ("<topic>.<group>.<field>").

/** HR systems reachable through the unified HR connector — informational logo strip. */
export const HR_TOOLS = [
  { code: "personio", name: "Personio", logo: "/logos/hr/personio.svg" },
  { code: "workday", name: "Workday" },
  { code: "successfactors", name: "SAP SuccessFactors", logo: "/logos/hr/sap.svg" },
  { code: "hibob", name: "HiBob", logo: "/logos/hr/hibob.svg" },
  { code: "bamboohr", name: "BambooHR" },
  { code: "abacus", name: "Abacus" },
  { code: "factorial", name: "Factorial" },
  { code: "datev", name: "DATEV", logo: "/logos/hr/datev.svg" },
  { code: "adp", name: "ADP", logo: "/logos/hr/adp.svg" },
  { code: "sage", name: "Sage HR", logo: "/logos/hr/sage.svg" },
  { code: "oracle", name: "Oracle HCM", logo: "/logos/hr/oracle.svg" },
  { code: "deel", name: "Deel" },
  { code: "rippling", name: "Rippling" },
  { code: "gusto", name: "Gusto", logo: "/logos/hr/gusto.svg" },
];

export const HR_METRIC_GROUPS = [
  { key: "workforce", label: "Headcount & contracts" },
  { key: "movement", label: "Hiring & leavers" },
  { key: "training", label: "Training & development" },
  { key: "safety", label: "Health & safety" },
  { key: "pay", label: "Pay & representation" },
];

export const HR_METRICS = [
  { code: "headcount_total", label: "Total employees", unit: "headcount", group: "workforce", fields: ["own_workforce.headcount.headcount_total"] },
  { code: "headcount_female", label: "Female employees", unit: "headcount", group: "workforce", fields: ["own_workforce.headcount.headcount_female"] },
  { code: "headcount_male", label: "Male employees", unit: "headcount", group: "workforce", fields: ["own_workforce.headcount.headcount_male"] },
  { code: "headcount_other", label: "Other / not stated", unit: "headcount", group: "workforce", fields: [] },
  { code: "permanent", label: "Permanent contracts", unit: "headcount", group: "workforce", fields: ["own_workforce.headcount.permanent"] },
  { code: "temporary", label: "Temporary contracts", unit: "headcount", group: "workforce", fields: ["own_workforce.headcount.temporary"] },
  { code: "full_time", label: "Full-time", unit: "headcount", group: "workforce", fields: [] },
  { code: "part_time", label: "Part-time", unit: "headcount", group: "workforce", fields: [] },

  { code: "new_hires", label: "New hires", unit: "headcount", group: "movement", fields: ["recruitment.hiring.new_hires"] },
  { code: "new_hires_female", label: "New hires — female", unit: "headcount", group: "movement", fields: ["recruitment.hiring.new_hires_female"] },
  { code: "open_positions", label: "Open positions at period end", unit: "count", group: "movement", fields: ["recruitment.hiring.open_positions"] },
  { code: "avg_time_to_hire_days", label: "Average time to hire", unit: "days", group: "movement", fields: ["recruitment.hiring.avg_time_to_hire_days"] },
  { code: "leavers", label: "Leavers", unit: "headcount", group: "movement", fields: ["own_workforce.headcount.leavers"] },
  { code: "leavers_voluntary", label: "Leavers — voluntary", unit: "headcount", group: "movement", fields: [] },
  { code: "turnover_pct", label: "Turnover rate", unit: "%", group: "movement", fields: ["own_workforce.headcount.turnover_pct"] },

  { code: "training_hours_total", label: "Training hours (total)", unit: "h", group: "training", fields: ["training.programmes.training_hours_total"] },
  { code: "training_hours_avg", label: "Training hours per employee", unit: "h", group: "training", fields: ["training.programmes.training_hours_avg", "own_workforce.pay_training.training_hours_avg"] },
  { code: "employees_trained_pct", label: "Employees who received training", unit: "%", group: "training", fields: ["training.programmes.employees_trained_pct"] },
  { code: "performance_reviews_pct", label: "Employees with a performance review", unit: "%", group: "training", fields: ["training.programmes.performance_reviews_pct"] },

  { code: "fatalities", label: "Fatalities", unit: "count", group: "safety", fields: ["health_safety.incidents.fatalities"] },
  { code: "recordable_injuries", label: "Recordable work-related accidents", unit: "count", group: "safety", fields: ["health_safety.incidents.recordable_injuries"] },
  { code: "hours_worked", label: "Hours worked", unit: "h", group: "safety", fields: ["health_safety.incidents.hours_worked"] },
  { code: "work_injury_days", label: "Days lost to work injury / ill health", unit: "days", group: "safety", fields: ["health_safety.incidents.days_lost"] },
  { code: "absence_days", label: "All absence days", unit: "days", group: "safety", fields: [] },

  { code: "gender_pay_gap_pct", label: "Gender pay gap", unit: "%", group: "pay", fields: ["own_workforce.pay_training.gender_pay_gap_pct"] },
  { code: "collective_bargaining_pct", label: "Covered by collective bargaining", unit: "%", group: "pay", fields: ["own_workforce.pay_training.collective_bargaining_pct"] },
];

export const metricByCode = (code) => HR_METRICS.find((m) => m.code === code) || null;

/** What kind of HR code the company is listing (HR_SYNC.HrCode.CodeKind). */
export const HR_CODE_KINDS = [
  { code: "CONTRACT_TYPE", label: "Contract type", hint: "e.g. PERM, FIXED, APPR" },
  { code: "EMPLOYEE_CATEGORY", label: "Employee category", hint: "e.g. FT, PT, INTERN" },
  { code: "ABSENCE_TYPE", label: "Absence type", hint: "e.g. SICK, ACC (work accident)" },
  { code: "LEAVER_REASON", label: "Leaver reason", hint: "e.g. RESIGN, RETIRE, DISMISS" },
  { code: "GENDER", label: "Gender code", hint: "e.g. F, M, D" },
  { code: "OTHER", label: "Other", hint: "" },
];

/** Topics whose forms can be filled from HR data. */
export const HR_TOPIC_KEYS = ["own_workforce", "health_safety", "training", "recruitment"];

/** Stored metrics → { "<topic>.<group>.<field>": value } for one topic. */
export const metricsToTopicValues = (metrics, topicKey) => {
  const out = {};
  (metrics || []).forEach((m) => {
    const def = metricByCode(m.metricCode);
    if (!def || m.value === null || m.value === undefined) return;
    def.fields
      .filter((f) => f.startsWith(`${topicKey}.`))
      .forEach((f) => { out[f.slice(topicKey.length + 1)] = Number(m.value); });
  });
  return out;
};

/**
 * Metrics → report-store sections (one per HR topic), each value with its provenance.
 * metrics = [{ metricCode, value, source, toolName }]
 */
export const metricsToReportSections = (metrics) => {
  const byTopic = {};
  (metrics || []).forEach((m) => {
    const def = metricByCode(m.metricCode);
    if (!def || m.value === null || m.value === undefined) return;
    def.fields.forEach((f) => {
      const [topicKey, ...rest] = f.split(".");
      (byTopic[topicKey] = byTopic[topicKey] || []).push({
        path: rest.join("."),
        value: String(Number(m.value)),
        unit: def.unit,
        sourceCode: m.source === "MANUAL" ? "HR" : "API_CONNECTOR",
        sourceRef: m.source === "MANUAL" ? `HR & Workforce · ${def.label} (entered)` : `${m.toolName || "HR system"} · ${def.label}`,
      });
    });
  });
  return Object.entries(byTopic).map(([topicKey, values]) => ({ topicKey, pillarCode: "S", values }));
};

/** Default reporting period: the last full calendar year. */
export const lastCalendarYear = () => {
  const y = new Date().getFullYear() - 1;
  return { from: `${y}-01-01`, to: `${y}-12-31` };
};
